import classNames from 'classnames';
import { CSSProperties } from 'react';
import { useTranslations } from 'next-intl';

// TODO(SP-669): restore alongside the commented-out "Learn how sharing works"
// link below — https://mindfeed.atlassian.net/browse/SP-669
// const ShareIcon = () => (
//   <svg viewBox="0 0 24 24" aria-hidden="true">
//     <circle cx="18" cy="5" r="3" />
//     <circle cx="6" cy="12" r="3" />
//     <circle cx="18" cy="19" r="3" />
//     <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
//   </svg>
// );

// Memo-type chip accents (tag pill background + text colour).
const CHIP_TONES = {
  quote: { background: '#FFE9F0', color: '#86314f', tagKey: 'tagQuote' },
  link: { background: '#BDDEFF', color: '#1c4a8f', tagKey: 'tagLink' },
  photo: { background: '#FDF6BB', color: '#7a6410', tagKey: 'tagPhoto' },
  list: { background: '#D1F2DD', color: '#1f6b46', tagKey: 'tagList' },
} as const;

type ChipTone = keyof typeof CHIP_TONES;

interface Chip {
  tone: ChipTone;
  /** i18n key suffix under pages.home.section19 for the chip text. */
  labelKey: string;
  /** Absolute placement + rotation on the cover. */
  pos: CSSProperties;
}

interface BoxConfig {
  id: string;
  gradient: string;
  /** Memory count; omit for the private box. */
  count?: number;
  private?: boolean;
  chips: [Chip, Chip];
}

const BOXES: BoxConfig[] = [
  {
    id: 'inspiration',
    gradient: 'linear-gradient(140deg,#FFF6C8,#FDEBA0)',
    count: 48,
    chips: [
      {
        tone: 'quote',
        labelKey: 'inspirationChip1',
        pos: { top: 16, left: 14, right: 30, transform: 'rotate(-2deg)' },
      },
      {
        tone: 'photo',
        labelKey: 'inspirationChip2',
        pos: { bottom: -6, right: 16, transform: 'rotate(3deg)' },
      },
    ],
  },
  {
    id: 'reading',
    gradient: 'linear-gradient(140deg,#D8ECFF,#B7D8FF)',
    count: 31,
    chips: [
      {
        tone: 'link',
        labelKey: 'readingChip1',
        pos: { top: 16, left: 14, right: 24, transform: 'rotate(2deg)' },
      },
      {
        tone: 'list',
        labelKey: 'readingChip2',
        pos: { bottom: -4, left: 22, transform: 'rotate(-3deg)' },
      },
    ],
  },
  {
    id: 'travel',
    gradient: 'linear-gradient(140deg,#FFE3EE,#FFC8DD)',
    count: 22,
    chips: [
      {
        tone: 'photo',
        labelKey: 'travelChip1',
        pos: { top: 16, right: 16, left: 30, transform: 'rotate(2deg)' },
      },
      {
        tone: 'quote',
        labelKey: 'travelChip2',
        pos: { bottom: -6, left: 16, transform: 'rotate(-2deg)' },
      },
    ],
  },
  {
    id: 'recipes',
    gradient: 'linear-gradient(140deg,#DFF5E8,#C2EBD3)',
    private: true,
    chips: [
      {
        tone: 'list',
        labelKey: 'recipesChip1',
        pos: { top: 16, left: 14, transform: 'rotate(-2deg)' },
      },
      {
        tone: 'photo',
        labelKey: 'recipesChip2',
        pos: { bottom: -4, right: 16, left: 30, transform: 'rotate(3deg)' },
      },
    ],
  },
];

const LockIcon = () => (
  <svg viewBox="0 0 24 24" className={styles.lockIcon} aria-hidden="true">
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

export const Section19 = () => {
  const t = useTranslations();

  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.head}>
          <div className={styles.eyebrow}>
            {t('pages.home.section19.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.home.section19.title', {
              highlight: (chunks) => (
                <span className={styles.emphasis}>{chunks}</span>
              ),
            })}
          </h2>
          <p className={styles.lead}>{t('pages.home.section19.lead')}</p>
        </div>

        <div className={styles.row}>
          {BOXES.map((box) => (
            <div key={box.id} className={styles.card}>
              <div
                className={styles.cover}
                style={{ background: box.gradient }}
              >
                {box.chips.map((chip) => {
                  const tone = CHIP_TONES[chip.tone];
                  return (
                    <div
                      key={chip.labelKey}
                      className={styles.chip}
                      style={chip.pos}
                    >
                      <span
                        className={styles.chipTag}
                        style={{
                          background: tone.background,
                          color: tone.color,
                        }}
                      >
                        {t(`pages.home.section19.${tone.tagKey}`)}
                      </span>
                      <span
                        className={classNames(
                          styles.chipText,
                          chip.tone === 'quote' && styles.chipQuote,
                        )}
                      >
                        {t(`pages.home.section19.${chip.labelKey}`)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className={styles.meta}>
                <span className={styles.name}>
                  {t(`pages.home.section19.${box.id}`)}
                </span>
                {box.private ? (
                  <span className={styles.lock}>
                    <LockIcon />
                    {t('pages.home.section19.private')}
                  </span>
                ) : (
                  <span className={styles.count}>
                    {t('pages.home.section19.memories', { count: box.count })}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/*
        // TODO(SP-669) https://mindfeed.atlassian.net/browse/SP-669 — learn links
        // temporarily hidden for launch.
        <div className={styles.learnWrap}>
          <LearnLink
            icon={<ShareIcon />}
            label={t('pages.home.section19.learnSharing')}
            href="#"
          />
        </div>
        */}
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy py-[88px] text-center',
  wrap: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  head: 'mx-auto mb-12 max-w-[640px]',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'mx-auto text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-4xl lg:text-[40px]',
  emphasis: 'text-mf-blue marketing-dark:text-mf-coral',
  lead: 'mx-auto mt-4 max-w-[580px] text-lg leading-[1.65] text-mf-gray-600 marketing-dark:text-white/66',
  row: 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4',
  // White showcase cards in both themes; light swaps the deep navy shadows
  // for a hairline + the soft floating-card tokens.
  card: classNames(
    'overflow-hidden rounded-[20px] bg-white text-left transition-all duration-200 hover:-translate-y-1.5',
    'border border-[#F0F0F0] shadow-[0_8px_22px_rgba(20,30,60,0.06)]',
    'hover:shadow-[0_14px_36px_rgba(20,30,60,0.07)]',
    'marketing-dark:border-0 marketing-dark:shadow-[0_20px_48px_rgba(2,8,30,0.42)]',
    'marketing-dark:hover:shadow-[0_30px_64px_rgba(2,8,30,0.52)]',
  ),
  cover: 'relative h-[118px] overflow-hidden p-3.5',
  chip: 'absolute rounded-[11px] bg-white px-[11px] py-[9px] text-[11px] font-semibold text-mf-off-black-600 shadow-[0_6px_16px_rgba(20,30,60,0.16)]',
  chipTag:
    'mb-[5px] inline-block rounded-full px-[6px] py-0.5 text-[8px] font-bold tracking-[0.07em]',
  chipText: 'block',
  chipQuote: 'font-serif text-[12px] italic leading-[1.35]',
  meta: 'flex items-center justify-between border-t border-mf-gray-200 px-[17px] pb-[17px] pt-[15px]',
  name: 'font-inter text-[16px] font-bold text-mf-off-black-600',
  count: 'text-[12.5px] font-medium text-mf-gray-500',
  lock: 'inline-flex items-center gap-[5px] text-[11px] font-semibold text-mf-gray-500',
  lockIcon: 'h-3 w-3 fill-none stroke-current stroke-2',
  learnWrap: 'mt-[38px] flex justify-center',
};
